import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { connectSocket } from '../../utils/socket';
import { toast } from 'react-toastify';
import { baseAPI } from '../../config';
import api, { getErrorMessage } from '../../services/api';
import userStore from '../../store/userStore';
import { useCallSessionStore, SESSION_STATUS } from '../../stores/callSessionStore';
import {
  changeAvailability,
  submitOutcome,
  sendHeartbeat,
  hangupCall,
  avisarDeLlamada,
  avisarDeAudio,
  playCue,
  avisarDeDescuelgue,
  avisarDeTipificar,
  avisarDeEntrante,
  crearTareaDeSeguimiento,
  cancelarTareaProgramada,
  HEARTBEAT_MS,
} from './callSessionController';
import { vigilar, COLGAR_SIN_VOZ_MS } from './vigilanteDeMicrofono';
import { grabar } from './cajaNegraDeAudio';
import CallSessionDock from './CallSessionDock';

/**
 * Lo que tiene que sobrevivir a cambiar de pantalla.
 *
 * Se monta **por encima del router**, no dentro de una ruta, y esa es toda la
 * idea: el socket, el latido y el elemento de audio vivían dentro de
 * `CallSessionPage`, así que al navegar se desmontaban y la sesión se moría sin
 * que el gestor hubiera pedido nada.
 *
 * Regla de producto, dicha por Edgar: **si el gestor no ha pausado a mano, esto
 * no se para**. Navegar no es pausar.
 */
const CallSessionHost = () => {
  const gestorId = userStore((s) => s.user?.id);
  const { pathname } = useLocation();

  const status = useCallSessionStore((s) => s.status);
  const current = useCallSessionStore((s) => s.current);
  const pendingOutcome = useCallSessionStore((s) => s.pendingOutcome);
  const accionesTrasTipificar = useCallSessionStore((s) => s.accionesTrasTipificar);
  const cerrarAcciones = useCallSessionStore((s) => s.cerrarAcciones);
  const queueDepth = useCallSessionStore((s) => s.queueDepth);
  const startDialing = useCallSessionStore((s) => s.startDialing);
  const setQueueDepth = useCallSessionStore((s) => s.setQueueDepth);
  const ended = useCallSessionStore((s) => s.ended);
  const puenteada = useCallSessionStore((s) => s.puenteada);
  const descartarTipificacion = useCallSessionStore((s) => s.descartarTipificacion);
  const recuperarTipificacion = useCallSessionStore((s) => s.recuperarTipificacion);
  const mediaWarning = useCallSessionStore((s) => s.mediaWarning);
  const screening = useCallSessionStore((s) => s.screening);
  const setScreening = useCallSessionStore((s) => s.setScreening);

  const activa = status !== SESSION_STATUS.OFFLINE;

  /**
   * El latido.
   *
   * ⚠️ Esto **no existía**: la ruta `/calls/sessions/heartbeat` estaba escrita en
   * el backend desde el primer día y no la llamaba nadie. `last_seen_at` solo se
   * escribía al activar sesión, así que a los dos minutos el marcador daba al
   * gestor por muerto y dejaba de mandarle llamadas **aunque estuviera delante de
   * la pantalla sin tocar nada**. Parecía que el marcador se paraba solo.
   */
  useEffect(() => {
    if (!activa) return undefined;
    sendHeartbeat();
    const id = setInterval(sendHeartbeat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [activa]);

  /**
   * El aviso llega por socket; la ficha, por HTTP.
   *
   * Quien marca es el bucle de pacing del backend, así que el front se entera de
   * que hay una marcación **antes de que exista la llamada**. Eso es lo que
   * permite pintar el briefing durante los tonos: esperar a la notificación de
   * Telnyx lo traería con la voz, o sea tarde.
   *
   * Por el socket solo viaja un aviso vacío. Desde el 22/10/2026 va con el token
   * y solo deja entrar en la sala propia, pero el nombre del deudor y su deuda se
   * siguen pidiendo por HTTP, y el backend comprueba que esa marcación es suya.
   */
  useEffect(() => {
    if (!activa || !gestorId) return undefined;

    const socket = connectSocket();
    socket.on('connect', () => socket.emit('join_call_session', gestorId));

    /**
     * Mide si sale audio, y SOLO mientras hay deudor al teléfono.
     *
     * 🔴 El tramo importa. Entre llamadas la pata del gestor se queda aparcada y
     * puede dejar de mandar bytes con toda legitimidad: vigilar en ese hueco
     * daría un aviso rojo en cada pausa, que es la forma más rápida de que el
     * gestor deje de creerse el aviso. Por eso arranca en `call_bridged` y para
     * en `call_ended`.
     *
     * Y va por socket, no por el cliente WebRTC, por lo mismo que el beep: con la
     * pata permanente el SDK solo avisa de la primera llamada de la jornada.
     */
    let pararVigilante = null;
    let temporizadorSinPitido = null;

    /**
     * La caja negra: graba TODA la sesión, no solo las llamadas.
     *
     * 🔴 El tramo es distinto del vigilante **a propósito**, y es la diferencia
     * entre encontrar la causa y no encontrarla. El vigilante solo mira mientras
     * hay deudor al teléfono, porque fuera de ahí avisaría en falso. Pero la
     * pata del gestor **vive toda la jornada**, y no sabemos si se rompe durante
     * una llamada o en el hueco entre dos. Grabar solo las llamadas dejaría el
     * instante de la rotura fuera justo en la mitad de los casos posibles.
     *
     * Arranca en cuanto hay pata de audio y no para hasta que se cierra la
     * sesión. No toca el micrófono: todo sale de `getStats()` y de propiedades
     * que ya están ahí.
     */
    let caja = null;
    const grabarSesion = () => {
      if (caja) return;
      const call = useCallSessionStore.getState().activeCall;
      if (!call) return;
      caja = grabar(call);
    };

    /**
     * Cuántas muestras caben en un volcado con `keepalive`.
     *
     * 🔴 **`keepalive` tiene un tope DURO de 64 KB de cuerpo**, del navegador, y
     * una petición que lo pasa se rechaza antes de salir. Es un límite de la
     * especificación, no una recomendación.
     *
     * Costó el primer volcado de fin de sesión del 15/10/2026: se mandaba con
     * `keepalive` por si la pestaña se estaba cerrando, pesaba varios cientos de
     * KB, y el navegador lo tiró. El `.catch` se lo comió en silencio y pareció
     * que el código no se había ejecutado. A ~340 bytes por muestra, 150 dejan el
     * cuerpo sobre los 50 KB, con margen para los eventos.
     */
    const MUESTRAS_CON_KEEPALIVE = 150;

    /**
     * Manda el volcado al servidor.
     *
     * Va por `fetch` y no por el cliente de axios **a propósito**: en el cierre
     * de la pestaña hace falta `keepalive` para que la petición sobreviva al
     * unload, y axios no lo expone.
     *
     * ⚠️ Pero `keepalive` **solo** cuando de verdad se cierra la pestaña, por el
     * tope de 64 KB de arriba. Cerrar la sesión de llamadas NO es cerrar la
     * pestaña: ahí va una petición normal, que no tiene tope y se lleva el
     * registro entero de la jornada — que es justo el volcado que más vale.
     *
     * Nunca lanza. Esto es diagnóstico: la llamada vale más que el diagnóstico
     * de la llamada.
     */
    const volcar = (motivo, { seCierraLaPestana = false } = {}) => {
      if (!caja) return;
      try {
        const grabado = caja.volcar();
        const cuerpo = {
          motivo,
          call_control_id: useCallSessionStore.getState().current?.callControlId || null,
          ...grabado,
          muestras: seCierraLaPestana
            ? grabado.muestras.slice(-MUESTRAS_CON_KEEPALIVE)
            : grabado.muestras,
        };
        fetch(`${baseAPI}/calls/audio-trace`, {
          method: 'POST',
          keepalive: seCierraLaPestana,
          headers: {
            'Content-Type': 'application/json',
            // De `localStorage` y no del store, igual que el interceptor de
            // `services/api`: es la fuente que usa el resto de la app, y al
            // cerrar la pestaña el store ya puede no estar.
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(cuerpo),
        }).catch(() => {});
      } catch {
        // Un volcado que falla no puede romper nada.
      }
    };

    /**
     * Si el gestor cierra la pestaña, salvar lo que se pueda.
     *
     * Va aquí dentro y no en el efecto del `beforeunload` de más abajo porque es
     * donde `caja` y `volcar` existen. Y lleva `keepalive` —el único sitio que lo
     * necesita, porque la pestaña se está muriendo—, con el recorte que impone su
     * tope de 64 KB.
     */
    const alCerrarLaPestana = () => volcar('fin_de_sesion', { seCierraLaPestana: true });
    window.addEventListener('beforeunload', alCerrarLaPestana);

    const vigilarSalida = () => {
      pararVigilante?.parar();
      grabarSesion();
      const call = useCallSessionStore.getState().activeCall;
      if (!call) return;
      caja?.apuntar('llamada_puenteada', {
        callControlId: useCallSessionStore.getState().current?.callControlId || null,
      });
      pararVigilante = vigilar(call, {
        onMudo: (motivo) => {
          useCallSessionStore.getState().setMediaWarning({ name: motivo, medidoPorNosotros: true });
          // 🔴 El volcado va AQUÍ, en el instante en que se detecta, no al
          // cerrar la sesión: para entonces el buffer ya puede haber dado la
          // vuelta y haberse comido el momento de la rotura.
          caja?.apuntar('VIGILANTE_AVISA', { motivo });
          volcar(motivo);
        },
      });
    };

    socket.on('call_dial_started', async () => {
      // En cuanto hay pata de audio, a grabar. Aquí y no solo en el puente: si
      // la pata se rompe ENTRE llamadas, empezar en `call_bridged` dejaría el
      // instante de la rotura fuera del volcado.
      grabarSesion();
      caja?.apuntar('marcando');
      try {
        const { data, status: httpStatus } = await api.get('/calls/sessions/current');
        if (httpStatus !== 204 && data) startDialing(data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    });

    socket.on('call_queue_depth', ({ depth }) => setQueueDepth(depth));

    /**
     * Hay una llamada terminada esperando a que el gestor la clasifique.
     *
     * 🔴 **El marcador está parado a propósito** mientras eso siga así: no se le
     * mete otra llamada encima a quien está cerrando la anterior. El fallo no era
     * pararse, era no decirlo — por fuera, un marcador que espera y uno averiado
     * se ven idénticos. Visto en producción el 29/09/2026: ocho marcaciones sin
     * tipificar y la sesión parada minutos, con el gestor en otra pantalla.
     *
     * El servidor lo reemite en cada barrido, y hace DOS cosas aquí:
     *
     *  1. **Recupera los botones** si se perdieron. `pendingOutcome` vive en
     *     memoria y el store no se persiste, así que una recarga los borra y deja
     *     al gestor sin ninguna forma de tipificar — parado para siempre y sin
     *     ningún error. `recuperarTipificacion` no pisa lo que ya haya.
     *  2. **Avisa por notificación del sistema**, que es el único camino
     *     comprobado que llega con la pestaña de fondo.
     */
    socket.on('call_typification_pending', (pendiente) => {
      recuperarTipificacion(pendiente);
      avisarDeTipificar(pendiente);
    });

    /**
     * Un deudor DEVUELVE la llamada.
     *
     * 🔴 El servidor ya la ha descolgado, pero hasta el 30/09/2026 no le decía
     * nada al gestor: ni ficha, ni aviso, ni sonido. «No he oído nada, no sabía
     * que me estaban llamando» — y la persona colgó.
     *
     * La ficha la pinta `call_dial_started`, que el servidor emite justo antes y
     * que este mismo efecto ya sabe atender. Esto es SOLO el aviso, y va aparte
     * porque tiene que ser distinto: una saliente da ~22 s de tono para
     * reaccionar y una entrante **no da ninguno**.
     *
     * `getState()` y no el `current` del render: el evento llega en el mismo
     * suspiro que `call_dial_started`, y la variable capturada por el efecto
     * todavía valdría `null` — el aviso saldría sin nombre justo cuando más
     * falta hace saber quién es.
     */
    socket.on('call_inbound', () => avisarDeEntrante(useCallSessionStore.getState().current));

    /**
     * Al otro lado hay un FILTRO de llamadas, no un contestador.
     *
     * Suenan igual —una locución— y sin decirlo el gestor cuelga creyendo que es
     * un buzón. Uno de cada cinco `machine` resultó ser esto al escuchar las
     * grabaciones del 24/09: gente localizable que se estaba tirando.
     */
    socket.on('call_screening_suspected', () => setScreening(true));

    /**
     * Telnyx ha terminado de analizar la locución **sin oír pitido**.
     *
     * 🔴 Esto es lo que decide si una llamada así se cuelga, y la decisión se
     * toma AQUÍ y no en el backend porque es el único sitio donde se sabe si el
     * gestor está hablando.
     *
     * Medido sobre las 8 primeras (26/10/2026): seis eran buzones que ocupaban
     * la línea ~121 s hasta que el contestador cortaba solo, y **una era una
     * conversación real de 932 s**. En el instante del aviso las ocho son
     * idénticas para el backend. Colgar al recibirlo sería volver a cortarle la
     * llamada a una persona, que es el fallo que se arregló el 23/10.
     *
     * Así que se espera, y **el silencio del gestor es el que cuelga**.
     */
    socket.on('call_sin_pitido', ({ callControlId }) => {
      clearTimeout(temporizadorSinPitido);
      const desde = Date.now();
      caja?.apuntar('sin_pitido', { callControlId });

      temporizadorSinPitido = setTimeout(() => {
        // ⚠️ **Que siga siendo LA MISMA llamada.** Sin esto, una llamada que
        // termina sola dentro de la ventana deja el temporizador vivo y lo que
        // se cuelga es la SIGUIENTE, con un deudor que acaba de descolgar.
        const actual = useCallSessionStore.getState().current?.callControlId || null;
        if (actual !== callControlId) return;

        // Sin medición no se cuelga. El «no sé» tiene que caer del lado de
        // dejar la llamada viva: equivocarse colgando le corta la conversación
        // a una persona; equivocarse esperando cuesta unos segundos de línea.
        if (pararVigilante?.sinMedicion !== false) return;
        if (pararVigilante.haHablado(desde)) return;

        caja?.apuntar('colgamos_por_silencio', { callControlId });
        hangupCall();
      }, COLGAR_SIN_VOZ_MS);
    });

    /**
     * Esta llamada ya está puenteada: el deudor y el gestor se oyen.
     *
     * 🔴 Viene por socket y no del cliente WebRTC porque **la pata de audio ya no
     * nace con la llamada**: se aparca entre llamadas para ahorrarle al deudor
     * ~5,5 s de silencio, así que el SDK solo avisa de la primera. Sin esto, de la
     * segunda en adelante la pantalla se quedaría en "Llamando…" con el gestor ya
     * hablando.
     */
    socket.on('call_bridged', () => {
      // 🔴 DOS avisos por caminos independientes, y es a propósito.
      //
      // Es el único instante por llamada en el que el gestor puede saber que tiene
      // a alguien al teléfono: con la pata de audio permanente, su cliente WebRTC
      // ya no le avisa de nada. Y el beep depende de la política de audio de
      // Chrome, que lo puede dejar mudo **sin dar error**.
      playCue();
      avisarDeDescuelgue(useCallSessionStore.getState().current);
      puenteada();
      avisarDeAudio();
      vigilarSalida();
    });

    /**
     * Red de seguridad: la llamada ha terminado.
     *
     * Normalmente el front se entera por su cliente WebRTC, que es quien tiene la
     * pata de audio. Pero si el puente no llega a construirse **esa pata no
     * existe**, así que el evento no llega nunca y la pantalla se queda en
     * "Llamando…" para siempre — con la sesión ocupada, o sea que el marcador
     * tampoco vuelve a llamar. Pasó en la primera prueba real del 23/09.
     *
     * `ended()` es idempotente en la práctica: si el cliente ya lo disparó, esto
     * solo repite el mismo estado.
     *
     * El `tipificable` del aviso es la única forma que tiene el front de saber
     * que descolgó un contestador: el veredicto del AMD llega por webhook, y el
     * cliente WebRTC de aquí no lo ve. Se descarta DESPUÉS de `ended()` y sin
     * depender de quién llegue antes —ver `descartarTipificacion`—.
     */
    socket.on('call_ended', (payload) => {
      clearTimeout(temporizadorSinPitido);
      pararVigilante?.parar();
      pararVigilante = null;
      caja?.apuntar('llamada_terminada', { tipificable: payload?.tipificable ?? null });
      ended();
      if (payload?.tipificable === false) descartarTipificacion(payload.callControlId);
    });

    return () => {
      clearTimeout(temporizadorSinPitido);
      pararVigilante?.parar();
      // El volcado de cierre: la sesión entera, aunque no haya saltado nada. Es
      // lo que permite comparar una jornada sana con una rota, que es la mitad
      // del diagnóstico — sin una referencia de «así se ve cuando va bien», un
      // valor raro en el volcado del fallo no se sabe si es la causa o lo normal.
      window.removeEventListener('beforeunload', alCerrarLaPestana);
      // Sin `keepalive`: cerrar la sesión de llamadas no es cerrar la pestaña, y
      // así va el registro ENTERO de la jornada, sin el tope de 64 KB.
      volcar('fin_de_sesion');
      caja?.parar();
      caja = null;
      socket.emit('leave_call_session', gestorId);
      socket.close();
    };
  }, [
    activa,
    gestorId,
    startDialing,
    setQueueDepth,
    ended,
    descartarTipificacion,
    recuperarTipificacion,
    setScreening,
    puenteada,
  ]);

  /**
   * Avisar antes de cerrar la pestaña.
   *
   * Cerrarla **sí** termina la sesión —el latido para y el backend la da por
   * muerta en dos minutos—, pero esos dos minutos el marcador sigue creyendo que
   * hay alguien: puede lanzar una llamada que nadie va a atender. Un aviso del
   * navegador no lo impide, pero evita el cierre por descuido, que es el caso
   * habitual.
   */
  useEffect(() => {
    if (!activa) return undefined;
    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [activa]);

  /**
   * Aviso del sistema al entrar una llamada.
   *
   * Es lo único de todo esto que **sale del navegador**. La barra flotante solo
   * existe mientras el gestor mira la pestaña de ClaimUp; si está en otra
   * aplicación, la llamada se descuelga sola y se encontraría hablando con
   * alguien sin saber quién es.
   */
  useEffect(() => {
    if (current) avisarDeLlamada(current);
  }, [current]);

  /**
   * El título de la pestaña dice lo que pasa.
   *
   * Se ve en la barra de pestañas **aunque el gestor esté en otra**, que es
   * justo cuando no puede ver ni la pantalla ni la barra flotante.
   */
  useEffect(() => {
    if (!activa) return undefined;

    const original = document.title;
    if (current) {
      document.title = `📞 ${current.debtorName || 'Llamada'} — ClaimUp`;
    } else if (status === SESSION_STATUS.READY) {
      document.title = '🟢 Sesión activa — ClaimUp';
    }

    return () => {
      document.title = original;
    };
  }, [activa, status, current]);

  // En su propia pantalla la barra sobra: allí está la completa.
  const enSuPantalla = pathname === '/call-session';

  return (
    <>
      {/*
        Donde el SDK engancha el audio remoto. Vive AQUÍ y no en la pantalla de
        sesión: `client.remoteElement` apunta a este id, y si lo pintara una ruta,
        navegar dejaría la conversación en curso sin dónde sonar.
      */}
      <audio id='call-audio' autoPlay />

      {activa && !enSuPantalla && (
        <CallSessionDock
          status={status}
          current={current}
          pendingOutcome={pendingOutcome}
          queueDepth={queueDepth}
          mediaWarning={mediaWarning}
          screening={screening}
          onPause={() => changeAvailability(SESSION_STATUS.PAUSED)}
          onResume={() => changeAvailability(SESSION_STATUS.READY)}
          onOutcome={submitOutcome}
          onHangup={hangupCall}
          accionesTrasTipificar={accionesTrasTipificar}
          onCrearTarea={crearTareaDeSeguimiento}
          onCancelarProgramada={cancelarTareaProgramada}
          onCerrarAcciones={cerrarAcciones}
        />
      )}
    </>
  );
};

export default CallSessionHost;
